// Открыть окно счёта при любой версии WebApp (живой Telegram Web, запуск кнопкой «Open»: tgWebAppVersion не
// передаётся, telegram-web-app.js считает версию 6.0, и WebApp.openInvoice (нужна 6.1+) бросает исключение ещё до
// вызова Telegram). Порядок: openInvoice → postEvent('web_app_open_invoice', {slug}) напрямую → openTelegramLink
// (только 6.1+: ниже он уводит саму страницу игры на t.me) → window.open. Чистая логика: окружение передаётся.
import { versionAtLeast, type InvoiceStatus } from '../telegram/webapp.js';

export type OpenPath = 'openInvoice' | 'postEvent' | 'tglink' | 'window' | 'none';

export interface InvoiceEnv {
  /** WebApp.openInvoice и проверка версии; нет WebApp — null. */
  wa: {
    openInvoice?: (url: string, cb?: (s: InvoiceStatus) => void) => void;
    openTelegramLink?: (url: string) => void;
    version: string;
    isVersionAtLeast?: (v: string) => boolean;
  } | null;
  /** Telegram.WebView из telegram-web-app.js: postEvent мимо проверки версии и сырые события. */
  webView: {
    postEvent?: (type: string, cb: unknown, data?: unknown) => void;
    onEvent?: (type: string, cb: (type: string, data: unknown) => void) => void;
    offEvent?: (type: string, cb: (type: string, data: unknown) => void) => void;
  } | null;
  windowOpen?: (url: string) => unknown;
}

/** slug счёта из ссылки Bot API: https://t.me/$slug или https://t.me/invoice/slug. */
export function invoiceSlug(url: string): string | null {
  const m = /^https?:\/\/t\.me\/(?:\$|invoice\/)([A-Za-z0-9_-]+)/.exec(url);
  return m ? m[1]! : null;
}

/**
 * Открыть счёт; статус (если путь его даёт) — в `cb`. Возвращает путь и текст исключения openInvoice, если было.
 * Исключение WebApp — не ошибка сервера: идём дальше по запасным путям.
 */
export function openInvoiceAny(env: InvoiceEnv, url: string, cb: (s: InvoiceStatus) => void): { path: OpenPath; error?: string } {
  let error: string | undefined;
  const wa = env.wa;
  const v61 = !!wa && versionAtLeast(wa, '6.1');
  if (wa?.openInvoice && v61) {
    try { wa.openInvoice(url, cb); return { path: 'openInvoice' }; } catch (e) { error = e instanceof Error ? e.message : String(e); }
  } else if (wa?.openInvoice) {
    error = 'version<6.1';
  } else if (!wa) {
    error = 'no_wa'; // WebApp нет (telegram-web-app.js не загрузился или без initData) — пробуем postEvent и ссылки
  }
  const slug = invoiceSlug(url);
  const wv = env.webView;
  if (slug && wv?.postEvent) {
    try {
      // Статус — сырым событием invoice_closed {slug, status}: WebApp его не перешлёт, счёт открыт не через него.
      if (wv.onEvent) {
        const h = (_t: string, d: unknown) => {
          const e = d as { slug?: string; status?: InvoiceStatus } | undefined;
          if (e?.slug === slug && e.status) { wv.offEvent?.('invoice_closed', h); cb(e.status); }
        };
        wv.onEvent('invoice_closed', h);
      }
      wv.postEvent('web_app_open_invoice', false, { slug });
      return { path: 'postEvent', ...(error ? { error } : {}) };
    } catch (e) { error = `${error ? `${error}; ` : ''}${e instanceof Error ? e.message : String(e)}`; }
  }
  if (wa?.openTelegramLink && v61) {
    try { wa.openTelegramLink(url); return { path: 'tglink', ...(error ? { error } : {}) }; } catch (e) { error = `${error ? `${error}; ` : ''}${e instanceof Error ? e.message : String(e)}`; }
  }
  if (env.windowOpen) {
    try { env.windowOpen(url); return { path: 'window', ...(error ? { error } : {}) }; } catch (e) { error = `${error ? `${error}; ` : ''}${e instanceof Error ? e.message : String(e)}`; }
  }
  return { path: 'none', ...(error ? { error } : {}) };
}
